import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaBolt, FaPowerOff, FaEdit } from 'react-icons/fa';
import api from '../services/api';

const DeviceManagement = () => {
    const [devices, setDevices] = useState([]);
    const [userIdToUsername, setUserIdToUsername] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeactivationModal, setShowDeactivationModal] = useState(false);
    const [selectedDeviceId, setSelectedDeviceId] = useState(null);
    const [deactivationDate, setDeactivationDate] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchDevicesAndUsers = async () => {
            try {
                setLoading(true);
                // Pobieramy urządzenia
                const devicesResponse = await api.get('/devices/');
                setDevices(devicesResponse.data);

                // Pobieramy użytkowników
                const usersResponse = await api.get('/users/');
                const users = usersResponse.data;

                // Tworzymy mapę user_id => username
                const userMap = {};
                users.forEach((user) => {
                    userMap[user.id] = user.username;
                });
                setUserIdToUsername(userMap);
                setError(null);
            } catch (error) {
                console.error('Błąd: Nie udało się pobrać danych urządzeń lub użytkowników.', error);
                setError('Nie udało się pobrać danych urządzeń lub użytkowników');
            } finally {
                setLoading(false);
            }
        };

        fetchDevicesAndUsers();
    }, []);

    const translateDeviceType = (type) => {
        switch (type) {
            case 'producer':
                return 'Producent';
            case 'consumer':
                return 'Konsument';
            case 'prosumer':
                return 'Prosument';
            default:
                return type;
        }
    };

    const openDeactivationModal = (deviceId) => {
        setSelectedDeviceId(deviceId);
        // Ustaw domyślną datę na pierwszy dzień następnego miesiąca
        const today = new Date();
        const firstDayNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        const formattedDate = firstDayNextMonth.toISOString().split('T')[0];
        setDeactivationDate(formattedDate);
        setShowDeactivationModal(true);
    };

    const closeDeactivationModal = () => {
        setShowDeactivationModal(false);
        setSelectedDeviceId(null);
        setDeactivationDate('');
    };

    const handleDeactivateDevice = async () => {
        try {
            const url = deactivationDate
                ? `/devices/${selectedDeviceId}/deactivate?deactivation_date=${deactivationDate}`
                : `/devices/${selectedDeviceId}/deactivate`;

            const response = await api.post(url);

            // Aktualizuj listę urządzeń z nową datą dezaktywacji
            setDevices(devices.map((device) =>
                device.id === selectedDeviceId
                    ? { ...device, deactivation_date: response.data.deactivation_date }
                    : device
            ));

            alert('Urządzenie PPE zostało dezaktywowane.');
            closeDeactivationModal();
        } catch (error) {
            console.error('Błąd podczas dezaktywacji PPE:', error);
            alert('Błąd podczas dezaktywacji PPE.');
        }
    };

    // Renderowanie karty dla widoku mobilnego
    const renderMobileDeviceList = () => {
        return devices.map((device) => (
            <div key={device.id} className="mb-4 p-4 border border-gray-200 rounded-lg bg-white shadow-sm">
                <div className="mb-2 flex items-center">
                    <FaBolt className="text-softTeal mr-2" />
                    <span className="font-bold">{device.name}</span>
                </div>
                <div className="grid grid-cols-1 gap-2 mb-3">
                    <div>
                        <span className="font-bold">ID:</span>
                        <span className="ml-2">{device.id}</span>
                    </div>
                    <div>
                        <span className="font-bold">Typ:</span>
                        <span className="ml-2">{translateDeviceType(device.device_type)}</span>
                    </div>
                    <div>
                        <span className="font-bold">Użytkownik:</span>
                        <span className="ml-2">{userIdToUsername[device.user_id] || 'Nieznany użytkownik'}</span>
                    </div>
                    <div>
                        <span className="font-bold">Data aktywacji:</span>
                        <span className="ml-2">{device.activation_date}</span>
                    </div>
                    {device.deactivation_date && (
                        <div>
                            <span className="font-bold">Data dezaktywacji:</span>
                            <span className="ml-2 text-red-600">{device.deactivation_date}</span>
                        </div>
                    )}
                </div>
            </div>
        ));
    };

    // Renderowanie tradycyjnej tabeli dla większych ekranów
    const renderDesktopTable = () => {
        return (
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th className="border px-4 py-2 text-left">Nazwa</th>
                        <th className="border px-4 py-2 text-left">ID</th>
                        <th className="border px-4 py-2 text-left">Typ</th>
                        <th className="border px-4 py-2 text-left">Nazwa użytkownika</th>
                        <th className="border px-4 py-2 text-left">Data aktywacji</th>
                        <th className="border px-4 py-2 text-left">Data dezaktywacji</th>
                        <th className="border px-4 py-2 text-left">Status</th>
                        <th className="border px-4 py-2 text-center">Akcje</th>
                    </tr>
                    </thead>
                    <tbody>
                    {devices.map((device) => (
                        <tr key={device.id}>
                            <td className="border px-4 py-2">{device.name}</td>
                            <td className="border px-4 py-2">{device.id}</td>
                            <td className="border px-4 py-2">
                                {translateDeviceType(device.device_type)}
                            </td>
                            <td className="border px-4 py-2">
                                {userIdToUsername[device.user_id] || 'Nieznany użytkownik'}
                            </td>
                            <td className="border px-4 py-2">{device.activation_date}</td>
                            <td className="border px-4 py-2">
                                {device.deactivation_date ? (
                                    <span className="text-red-600">{device.deactivation_date}</span>
                                ) : (
                                    <span className="text-gray-400">-</span>
                                )}
                            </td>
                            <td className="border px-4 py-2">
                                {device.deactivation_date ? (
                                    <span className="text-red-600 font-semibold">Nieaktywny</span>
                                ) : (
                                    <span className="text-green-600 font-semibold">Aktywny</span>
                                )}
                            </td>
                            <td className="border px-4 py-2 text-center">
                                <div className="flex justify-center gap-2">
                                    <button
                                        onClick={() => navigate(`/admin/devices/edit/${device.id}`)}
                                        className="text-softTeal hover:text-warmOrange p-1"
                                        title="Edytuj"
                                    >
                                        <FaEdit />
                                    </button>
                                    {!device.deactivation_date && (
                                        <button
                                            onClick={() => openDeactivationModal(device.id)}
                                            className="text-softTeal hover:text-warmOrange p-1"
                                            title="Dezaktywuj"
                                        >
                                            <FaPowerOff />
                                        </button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="min-h-screen p-4">
            <main className="container mx-auto">
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl sm:text-2xl font-bold text-darkPurple">PPE</h2>
                        <button
                            className="bg-softTeal text-pureWhite font-semibold px-4 py-2 rounded-lg hover:bg-warmOrange transition flex items-center"
                            onClick={() => navigate('/admin/devices/create')}
                        >
                            <FaPlus className="mr-2" /> Dodaj PPE
                        </button>
                    </div>

                    {loading ? (
                        <p className="text-center py-4">Ładowanie urządzeń PPE...</p>
                    ) : error ? (
                        <p className="text-red-500 text-center py-4">{error}</p>
                    ) : devices.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">Brak urządzeń PPE</p>
                    ) : (
                        <>
                            <div className="md:hidden">
                                {renderMobileDeviceList()}
                            </div>
                            
                            <div className="hidden md:block">
                                {renderDesktopTable()}
                            </div>
                        </>
                    )}
                </div>
            </main>

            {/* Modal dezaktywacji */}
            {showDeactivationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-xl font-bold text-darkPurple mb-4">
                            Dezaktywacja urządzenia PPE
                        </h3>
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Data dezaktywacji
                            </label>
                            <input
                                type="date"
                                value={deactivationDate}
                                onChange={(e) => setDeactivationDate(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-softTeal focus:border-transparent"
                            />
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={closeDeactivationModal}
                                className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-semibold"
                            >
                                Anuluj
                            </button>
                            <button
                                onClick={handleDeactivateDevice}
                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-semibold flex items-center"
                            >
                                <FaPowerOff className="mr-2" /> Dezaktywuj
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeviceManagement;
